/**
 * InvoiceRowActions — Phase 2C.M+ (2026-09-07)
 *
 * Menu d'actions pour une ligne facture (liste `/factures`).
 * Symétrique de `QuoteRowActions.jsx` (matrice `{on, hide?, tip?}` par statut).
 *
 * Complète le geste "renvoyer par email" livré côté backend au commit d2ac707
 * (2026-09-06) mais qui n'avait AUCUNE exposition UI dans la liste factures —
 * seule la fiche chantier (`ChantierInvoicesSection.jsx`) l'exposait.
 *
 * Actions :
 *   - Voir le PDF (nouvel onglet, tous statuts)
 *   - Renvoyer par email (pending/overdue) avec email_override optionnel
 *   - Marquer payée (pending/overdue) — délégué au parent via `onMarkPaid`
 */
import { useState } from "react";
import { toast } from "sonner";
import { MoreHorizontal, Mail, CheckCircle2, Loader2, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ViewPdfLink } from "@/components/ViewPdfLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

// Matrice statut × action — Phase Semi-Auto Facture (12/09/2026)
// Nouveaux statuts : `a_envoyer` (première expédition), `envoyee` (post-envoi).
// - `send` : premier envoi manuel (`POST /invoices/{id}/send`), visible UNIQUEMENT sur `a_envoyer`.
// - `resend` : renvoi ultérieur, visible sur `envoyee` + `overdue` (backend gate identique).
const VIS = {
  a_envoyer: {
    pdf: { on: true },
    send: { on: true },
    resend: { on: false, hide: true },
    markPaid: { on: false, hide: true },
  },
  envoyee: {
    pdf: { on: true },
    send: { on: false, hide: true },
    resend: { on: true },
    markPaid: { on: true },
  },
  overdue: {
    pdf: { on: true },
    send: { on: false, hide: true },
    resend: { on: true, tip: "Facture en retard — le client verra l'échéance dépassée." },
    markPaid: { on: true },
  },
  paid: {
    pdf: { on: true },
    send: { on: false, hide: true },
    resend: { on: false, hide: true },
    markPaid: { on: false, hide: true },
  },
};

export default function InvoiceRowActions({ invoice, onChanged }) {
  const vis = VIS[invoice.status] || VIS.a_envoyer;
  const [dlgResend, setDlgResend] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  // BLOC 2026-10-18 20h55 — Pré-fetch href PDF au moment où le menu s'ouvre.
  // Pas de window.open, pas d'await dans le onClick ; `<ViewPdfLink>` rend un
  // `<a href>` natif iPhone-safe (voir useSignedPdfHref.js).
  const [menuOpen, setMenuOpen] = useState(false);

  const openResend = (e) => {
    e.stopPropagation();
    setResendEmail(invoice.client_email || "");
    setDlgResend(true);
  };

  const submitResend = async () => {
    setResendBusy(true);
    try {
      const body = resendEmail && resendEmail !== invoice.client_email
        ? { email_override: resendEmail.trim() }
        : {};
      await api.post(`/invoices/${invoice.id}/resend`, body);
      toast.success("Facture renvoyée par email");
      setDlgResend(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResendBusy(false);
    }
  };

  const doMarkPaid = async (e) => {
    e.stopPropagation();
    try {
      await api.post(`/invoices/${invoice.id}/mark-paid`, {});
      toast.success("Facture marquée payée");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Phase Semi-Auto Facture (12/09/2026) — Premier envoi manuel
  const doSend = async (e) => {
    e.stopPropagation();
    try {
      const res = await api.post(`/invoices/${invoice.id}/send`, {});
      toast.success(`Facture envoyée à ${res.data.email}`);
      onChanged?.();
    } catch (err) {
      toast.error("Envoi impossible : " + formatApiError(err));
    }
  };

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            onClick={(e) => e.stopPropagation()}
            className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
            data-testid={`invoice-actions-menu-${invoice.id}`}
            aria-label="Actions facture"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="border-slate-700 bg-slate-900"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuItem asChild disabled={!vis.pdf.on}>
            <ViewPdfLink
              entityType="invoice"
              entityId={invoice.id}
              enabled={menuOpen && vis.pdf.on}
              label="Voir le PDF"
              testid={`invoice-action-pdf-${invoice.id}`}
              as="dropdown-item"
              disabled={!vis.pdf.on}
              title={vis.pdf.on ? undefined : vis.pdf.tip}
            />
          </DropdownMenuItem>

          {!vis.send.hide && (
            <DropdownMenuItem
              onClick={doSend}
              disabled={!vis.send.on}
              title={vis.send.on ? undefined : vis.send.tip}
              data-testid={`invoice-action-send-${invoice.id}`}
              className="text-emerald-400 focus:bg-emerald-900/20 focus:text-emerald-300"
            >
              <Send className="mr-2 h-4 w-4" /> Envoyer par email
            </DropdownMenuItem>
          )}

          {!vis.resend.hide && (
            <DropdownMenuItem
              onClick={openResend}
              disabled={!vis.resend.on}
              title={vis.resend.on ? vis.resend.tip : (vis.resend.tip || "")}
              data-testid={`invoice-action-resend-${invoice.id}`}
            >
              <Mail className="mr-2 h-4 w-4" /> Renvoyer par email
            </DropdownMenuItem>
          )}

          {!vis.markPaid.hide && (
            <DropdownMenuItem
              onClick={doMarkPaid}
              disabled={!vis.markPaid.on}
              title={vis.markPaid.on ? undefined : vis.markPaid.tip}
              data-testid={`invoice-action-mark-paid-${invoice.id}`}
            >
              <CheckCircle2 className="mr-2 h-4 w-4" /> Marquer payée
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={dlgResend} onOpenChange={setDlgResend}>
        <DialogContent
          className="max-w-md"
          data-testid={`invoice-resend-dialog-${invoice.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>Renvoyer la facture N° {invoice.number || "—"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-400">
              Le PDF sera renvoyé au client. Le numéro et la date d'émission
              restent inchangés.
            </p>
            <div>
              <Label htmlFor={`invoice-resend-email-${invoice.id}`}>
                Adresse email destinataire
              </Label>
              <Input
                id={`invoice-resend-email-${invoice.id}`}
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="client@exemple.be"
                className="mt-1"
                data-testid={`invoice-resend-email-input-${invoice.id}`}
              />
              <p className="mt-1 text-xs text-slate-500">
                Optionnel — laissez vide pour utiliser l'email du contact.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDlgResend(false)}
              disabled={resendBusy}
              data-testid={`invoice-resend-cancel-${invoice.id}`}
            >
              Annuler
            </Button>
            <Button
              onClick={submitResend}
              disabled={resendBusy}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid={`invoice-resend-submit-${invoice.id}`}
            >
              {resendBusy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Envoi…
                </>
              ) : (
                "Envoyer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
